import React from 'react';
import { Checkbox } from '@/components/ui/Checkbox';
import { GEO_TARGET_OPTIONS, LANGUAGE_OPTIONS } from './networkOptions';

export interface NetworkSettings {
  targetGoogleSearch: boolean;
  targetSearchNetwork: boolean;
  targetContentNetwork: boolean;
}

export interface TargetingSettings {
  geoTargetIds: number[];
  languageIds: number[];
}

interface NetworkSettingsStepProps {
  networkSettings: NetworkSettings;
  targetingSettings: TargetingSettings;
  onChangeNetworkSettings: (settings: NetworkSettings) => void;
  onChangeTargetingSettings: (settings: TargetingSettings) => void;
}

export const NetworkSettingsStep: React.FC<NetworkSettingsStepProps> = ({
  networkSettings,
  targetingSettings,
  onChangeNetworkSettings,
  onChangeTargetingSettings,
}) => {
  const handleToggle = (field: keyof NetworkSettings) => {
    onChangeNetworkSettings({
      ...networkSettings,
      [field]: !networkSettings[field],
    });
  };

  const handleGeoToggle = (geoId: number) => {
    const newGeoIds = targetingSettings.geoTargetIds.includes(geoId)
      ? targetingSettings.geoTargetIds.filter((id) => id !== geoId)
      : [...targetingSettings.geoTargetIds, geoId];

    onChangeTargetingSettings({
      ...targetingSettings,
      geoTargetIds: newGeoIds,
    });
  };

  const handleLanguageToggle = (langId: number) => {
    const newLangIds = targetingSettings.languageIds.includes(langId)
      ? targetingSettings.languageIds.filter((id) => id !== langId)
      : [...targetingSettings.languageIds, langId];

    onChangeTargetingSettings({
      ...targetingSettings,
      languageIds: newLangIds,
    });
  };

  const handleSelectAllEnglishSpeaking = () => {
    // USA, UK, Canada, Australia, Ireland, New Zealand
    const englishCountries = [2840, 2826, 2124, 2036, 2372, 2554];
    onChangeTargetingSettings({
      geoTargetIds: englishCountries,
      languageIds: [1000], // English
    });
  };

  return (
    <div className="space-y-8">
      {/* Location Targeting Section */}
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Location Targeting</h3>
          <p className="text-sm text-gray-600">
            Choose which countries or regions to show your ads in.
          </p>
        </div>

        {/* Quick select buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => onChangeTargetingSettings({ ...targetingSettings, geoTargetIds: [2840] })}
            className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
          >
            USA Only
          </button>
          <button
            type="button"
            onClick={handleSelectAllEnglishSpeaking}
            className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
          >
            English-Speaking Countries
          </button>
          <button
            type="button"
            onClick={() => onChangeTargetingSettings({ ...targetingSettings, geoTargetIds: GEO_TARGET_OPTIONS.map(g => g.id) })}
            className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
          >
            Select All
          </button>
          <button
            type="button"
            onClick={() => onChangeTargetingSettings({ ...targetingSettings, geoTargetIds: [] })}
            className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
          >
            Clear All
          </button>
        </div>

        {/* Country grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-3 border border-gray-200 rounded-lg bg-gray-50">
          {GEO_TARGET_OPTIONS.map((geo) => (
            <label
              key={geo.id}
              className={`flex items-center p-2 rounded cursor-pointer transition-colors ${
                targetingSettings.geoTargetIds.includes(geo.id)
                  ? 'bg-blue-50 border border-blue-200'
                  : 'bg-white border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Checkbox
                checked={targetingSettings.geoTargetIds.includes(geo.id)}
                onChange={() => handleGeoToggle(geo.id)}
              />
              <span className="ml-2 text-sm text-gray-700">{geo.name}</span>
            </label>
          ))}
        </div>

        {targetingSettings.geoTargetIds.length === 0 && (
          <p className="mt-2 text-sm text-amber-600">
            Please select at least one location. USA will be used as default if none selected.
          </p>
        )}
      </div>

      {/* Language Targeting Section */}
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Language Targeting</h3>
          <p className="text-sm text-gray-600">
            Choose which languages your customers speak.
          </p>
        </div>

        {/* Language grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-3 border border-gray-200 rounded-lg bg-gray-50">
          {LANGUAGE_OPTIONS.map((lang) => (
            <label
              key={lang.id}
              className={`flex items-center p-2 rounded cursor-pointer transition-colors ${
                targetingSettings.languageIds.includes(lang.id)
                  ? 'bg-blue-50 border border-blue-200'
                  : 'bg-white border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Checkbox
                checked={targetingSettings.languageIds.includes(lang.id)}
                onChange={() => handleLanguageToggle(lang.id)}
              />
              <span className="ml-2 text-sm text-gray-700">{lang.name}</span>
            </label>
          ))}
        </div>

        {targetingSettings.languageIds.length === 0 && (
          <p className="mt-2 text-sm text-amber-600">
            Please select at least one language. English will be used as default if none selected.
          </p>
        )}
      </div>

      {/* Network Settings Section */}
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Network Settings</h3>
          <p className="text-sm text-gray-600">
            Choose where you want your ads to appear across Google's networks.
          </p>
        </div>

        <div className="space-y-3">
          {/* Google Search */}
          <div className="p-3 border border-gray-200 rounded-lg">
            <label className="flex items-start cursor-pointer">
              <Checkbox
                checked={networkSettings.targetGoogleSearch}
                onChange={() => handleToggle('targetGoogleSearch')}
                aria-describedby="google-search-description"
              />
              <div className="ml-3 flex-1">
                <span className="block text-sm font-medium text-gray-900">Google Search</span>
                <p id="google-search-description" className="text-xs text-gray-500 mt-0.5">
                  Show ads on Google search results pages. Highly recommended.
                </p>
              </div>
            </label>
          </div>

          {/* Search Partners */}
          <div className="p-3 border border-gray-200 rounded-lg">
            <label className="flex items-start cursor-pointer">
              <Checkbox
                checked={networkSettings.targetSearchNetwork}
                onChange={() => handleToggle('targetSearchNetwork')}
                aria-describedby="search-partners-description"
              />
              <div className="ml-3 flex-1">
                <span className="block text-sm font-medium text-gray-900">Search Partners</span>
                <p id="search-partners-description" className="text-xs text-gray-500 mt-0.5">
                  Show ads on YouTube and other Google partner sites.
                </p>
              </div>
            </label>
          </div>

          {/* Display Network */}
          <div className="p-3 border border-gray-200 rounded-lg">
            <label className="flex items-start cursor-pointer">
              <Checkbox
                checked={networkSettings.targetContentNetwork}
                onChange={() => handleToggle('targetContentNetwork')}
                aria-describedby="display-network-description"
              />
              <div className="ml-3 flex-1">
                <span className="block text-sm font-medium text-gray-900">Display Network</span>
                <p id="display-network-description" className="text-xs text-gray-500 mt-0.5">
                  Show ads on websites, apps, and videos across the Display Network.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Warning if Google Search disabled */}
        {!networkSettings.targetGoogleSearch && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <svg
                className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-xs text-yellow-700">
                Disabling Google Search is not recommended. Your campaign may receive limited traffic.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
